import { SCHEMATIC_EXAMPLES } from "./schematic-library";
import { type SchematicExampleId } from "./schematic-library-routes";
/**
 * A guide's worked example — or the front page's live drawing — handed to the
 * editor across the navigation to `/app`.
 *
 * The ordinary page-to-canvas hop uses `sessionStorage`. Only when the visitor
 * explicitly starts authentication do we copy a 30-minute handoff to
 * `localStorage`, because magic links commonly open in a new tab.
 *
 * Fixed examples carry id/variant. Calculator arrivals additionally carry a
 * versioned, bounded input contract which is revalidated and recalculated.
 * Every system is rebuilt by our own builder; arbitrary system payloads are
 * never accepted through this handoff.
 */

import { heroSystem } from "./hero-system";
import { heroPortraitSystem } from "./hero-portrait-system";
import {
  buildLeadAcidBeforeSystem,
  buildLifepo4AfterSystem,
} from "./lifepo4-conversion-systems";
import type { System } from "./types";
import { isUnitSystem, type UnitSystem } from "./units/units";
import { WIRING_EXAMPLE_OPTIONS, type WiringExampleId, type WiringExampleVariant } from "./wiring-diagram-systems";
import { calculatorSystem, parseCalculatorHandoff, type CalculatorHandoff } from "./calculator-systems";
import { TOOL_EXAMPLES, type ToolExampleId } from "./tool-example-systems";

const KEY = "crimp:resource-example-handoff";
const AUTH_KEY = "crimp:resource-example-auth-handoff:v1";
const AUTH_HANDOFF_MAX_AGE_MS = 30 * 60 * 1000;

type FixedExampleId = "lifepo4-conversion" | "landing-hero" | "tool-example" | WiringExampleId | SchematicExampleId;
export type ResourceExampleId = FixedExampleId | CalculatorHandoff["example"];
/** `default` is the variant of an example that has only one drawing. */
export type ResourceExampleVariant = "before" | "after" | "default" | "portrait" | "boat-portrait" | WiringExampleVariant | ToolExampleId;

export type ResourceExampleHandoff = CalculatorHandoff | {
  example: FixedExampleId;
  variant: ResourceExampleVariant;
  units?: UnitSystem;
};

type ExampleEntry = { name?: string; build: () => System };

const EXAMPLES: Record<
  FixedExampleId,
  Partial<Record<ResourceExampleVariant, ExampleEntry>>
> = {
  ...Object.fromEntries(SCHEMATIC_EXAMPLES.map(example => [example.id, { default: { build: example.build } }])) as Record<SchematicExampleId, { default: ExampleEntry }>,
  "tool-example": TOOL_EXAMPLES,
  ...Object.fromEntries(Object.entries(WIRING_EXAMPLE_OPTIONS).map(([id, options]) => [id,
    Object.fromEntries(options.map((option) => [option.variant, { build: option.build }])),
  ])) as Record<WiringExampleId, Partial<Record<ResourceExampleVariant, ExampleEntry>>>,
  "lifepo4-conversion": {
    before: {
      name: "LiFePO₄ conversion example — before",
      build: buildLeadAcidBeforeSystem,
    },
    after: {
      name: "LiFePO₄ conversion example — after",
      build: buildLifepo4AfterSystem,
    },
  },
  // The drawing on the front page, so "open this example" lands the visitor
  // on the exact system they were just reading numbers off.
  "landing-hero": {
    default: {
      name: "Camper van example — solar, fridge and lights",
      build: heroSystem,
    },
    portrait: {
      name: "Camper van example — solar, fridge and lights",
      build: heroPortraitSystem,
    },
    "boat-portrait": {
      name: "Boat wiring example — solar, fridge and lights",
      build: heroPortraitSystem,
    },
  },
};

function isExampleId(value: unknown): value is FixedExampleId {
  return typeof value === "string" && Object.hasOwn(EXAMPLES, value);
}

function isVariant(value: unknown): value is ResourceExampleVariant {
  return typeof value === "string" && (["before", "after", "default", "portrait", "boat-portrait", "house", "starter-house", "simple", "solar-alternator", "orion", "redarc"].includes(value) || Object.hasOwn(TOOL_EXAMPLES, value));
}

/** Public identity only. This does not validate calculator inputs or authorize
 * loading a system; use the complete handoff parser for that. */
export function isResourceExampleIdentity(example: unknown, variant: unknown): boolean {
  if (example === "wire-size" || example === "dc-dc-charger-size" || example === "voltage-drop") {
    return variant === "calculated";
  }
  return isExampleId(example) && isVariant(variant) && Object.hasOwn(EXAMPLES[example], variant);
}

/** Validate the full request, then discard all entered values and units. */
export function resourceExampleIdentity(handoff: unknown): { id: string; variant: string } | null {
  try {
    const parsed = parseHandoff(JSON.stringify(handoff));
    return parsed ? { id: parsed.example, variant: parsed.variant } : null;
  } catch { return null; }
}

export function stashResourceExampleHandoff(
  handoff: ResourceExampleHandoff,
): boolean {
  const valid = parseHandoff(JSON.stringify(handoff));
  if (!valid) return false;
  try {
    const serialized = JSON.stringify(valid);
    sessionStorage.setItem(KEY, serialized);
    // A caller may use a clean editor URL only when this exact request was
    // retained. Never mistake an older cross-tab auth handoff for this write.
    return sessionStorage.getItem(KEY) === serialized;
  } catch {
    // Callers with explicit example URLs can still open the requested starter.
    return false;
  }
}

export function clearResourceExampleHandoff(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Ignore inaccessible storage.
  }
  try {
    localStorage.removeItem(AUTH_KEY);
  } catch {
    // Ignore inaccessible storage.
  }
}

/** Magic links commonly open a new tab. Promote only an explicit, pending
 * resource request when the user actually chooses to authenticate. */
export function promoteResourceExampleHandoffForAuth(
  handoffAt = Date.now(),
): void {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return;
    const handoff = parseHandoff(raw);
    if (!handoff) return;
    localStorage.setItem(
      AUTH_KEY,
      JSON.stringify({ handoff, handoffAt }),
    );
  } catch {
    // The original tab still retains its session-scoped request.
  }
}

function parseHandoff(raw: string): ResourceExampleHandoff | null {
  try {
    if (raw.length > 6000) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { example, variant, units } = parsed as Record<string, unknown>;
    if (example === "wire-size" || example === "dc-dc-charger-size" || example === "voltage-drop") return parseCalculatorHandoff(parsed);
    if (Object.hasOwn(parsed, "parameters")) return null;
    if (!isExampleId(example) || !isVariant(variant)) return null;
    if (!Object.hasOwn(EXAMPLES[example], variant)) return null;
    if (units !== undefined && !isUnitSystem(units)) return null;
    return { example, variant, ...(isUnitSystem(units) ? { units } : {}) };
  } catch {
    return null;
  }
}

function readAuthHandoff(consume: boolean, now = Date.now()): ResourceExampleHandoff | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    if (consume) localStorage.removeItem(AUTH_KEY);
    const parsed = JSON.parse(raw) as {
      handoff?: unknown;
      handoffAt?: unknown;
    };
    if (
      typeof parsed.handoffAt !== "number" ||
      !Number.isFinite(parsed.handoffAt) ||
      parsed.handoffAt > now + 60_000 ||
      now - parsed.handoffAt > AUTH_HANDOFF_MAX_AGE_MS
    ) {
      return null;
    }
    return parseHandoff(JSON.stringify(parsed.handoff));
  } catch {
    return null;
  }
}

/** Removes before parsing, so a key holding junk can't wedge every boot of the tab. */
export function takeResourceExampleHandoff(): ResourceExampleHandoff | null {
  return readResourceExampleHandoff(true);
}

/** Classify an attempted entry without taking the request from the loader.
 * Shares the exact session/auth validation and precedence with the consumer;
 * even invalid or expired stored values remain untouched during inspection. */
export function peekResourceExampleHandoff(now = Date.now()): ResourceExampleHandoff | null {
  return readResourceExampleHandoff(false, now);
}

function readResourceExampleHandoff(consume: boolean, now = Date.now()): ResourceExampleHandoff | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (consume) sessionStorage.removeItem(KEY);
  } catch {
    return readAuthHandoff(consume, now);
  }
  if (!raw) return readAuthHandoff(consume, now);
  const handoff = parseHandoff(raw);
  if (!handoff) return readAuthHandoff(consume, now);
  if (consume) {
    try {
      localStorage.removeItem(AUTH_KEY);
    } catch {
      // The session-scoped request is authoritative in this tab.
    }
  }
  return handoff;
}

/**
 * The system to open, named for the project it becomes. The name is written
 * onto the system rather than passed alongside it: the local repository takes
 * a saved project's name from `system.name`, so a name kept on the side would
 * be overwritten by the first autosave.
 */
export function resourceExampleSystem(
  handoff: ResourceExampleHandoff,
): System | null {
  if (!parseHandoff(JSON.stringify(handoff))) return null;
  if (handoff.example === "wire-size" || handoff.example === "dc-dc-charger-size" || handoff.example === "voltage-drop") return calculatorSystem(handoff);
  const entry = EXAMPLES[handoff.example]?.[handoff.variant];
  if (!entry) return null;
  const system = entry.build();
  return entry.name ? { ...system, name: entry.name } : system;
}

/** A real link survives disabled storage, pre-hydration clicks and new tabs. */
export function resourceExampleUrl(handoff: ResourceExampleHandoff): string {
  if (!parseHandoff(JSON.stringify(handoff))) return "/app";
  const params = new URLSearchParams({ example: handoff.example, variant: handoff.variant });
  if (handoff.units) params.set("units", handoff.units);
  if ("parameters" in handoff) params.set("calculator", JSON.stringify(handoff.parameters));
  return `/app?${params}`;
}

export function resourceExampleFromUrl(url: URL): ResourceExampleHandoff | null {
  try {
    for (const key of ["example", "variant", "units", "calculator"]) if (url.searchParams.getAll(key).length > 1) return null;
    const calculator = url.searchParams.get("calculator");
    if (calculator && calculator.length > 5000) return null;
    return parseHandoff(JSON.stringify({ example: url.searchParams.get("example"), variant: url.searchParams.get("variant"),
      ...(url.searchParams.has("units") ? { units: url.searchParams.get("units") } : {}),
      ...(url.searchParams.has("calculator") ? { parameters: JSON.parse(calculator!) } : {}),
    }));
  } catch { return null; }
}

/** The whole hop in one call, for the boot path that owns the arrival. */
export function takeResourceExampleSystem(): System | null {
  const handoff = takeResourceExampleHandoff();
  return handoff ? resourceExampleSystem(handoff) : null;
}
